import {
  CloseOutlined,
  DownOutlined,
  SearchOutlined,
  UpOutlined
} from '@ant-design/icons';
import { Button, Checkbox, Dropdown, Input, Segmented } from 'antd';
import { Tag } from 'lucide-react';
// eslint-disable-next-line no-use-before-define
import * as React from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import { useProjectUserDropdownHeight } from './ProjectUserPicker';
import { ProjectSearchFilters } from './tokens';
import styles from './filters.module.css';

export type ProjectSearchTag = {
  id: string;
  label: string;
};

type Props = {
  filters: ProjectSearchFilters;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  isContainerOpen: boolean;
  onChange: (filters: ProjectSearchFilters) => void;
  tags: ProjectSearchTag[];
};

const MAX_DROPDOWN_HEIGHT = 360;
const FIXED_CONTENT_HEIGHT = 126;
const MAX_LIST_HEIGHT = 240;
const ROW_HEIGHT = 40;

const toggleToken = (tokens: string[], token: string) =>
  tokens.includes(token)
    ? tokens.filter(value => value !== token)
    : [...tokens, token];

export const filterProjectSearchTags = (
  tags: ProjectSearchTag[],
  query: string,
  selectedTagIds: string[] = []
) => {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const selectedTags = new Set(selectedTagIds);
  const visibleTags = normalizedQuery
    ? tags.filter(tag =>
        tag.label.toLocaleLowerCase().includes(normalizedQuery)
      )
    : [...tags];

  return visibleTags.sort((firstTag, secondTag) => {
    const selectedOrder =
      Number(!selectedTags.has(firstTag.id)) -
      Number(!selectedTags.has(secondTag.id));

    if (selectedOrder) return selectedOrder;

    return firstTag.label
      .toLocaleLowerCase()
      .localeCompare(secondTag.label.toLocaleLowerCase());
  });
};

const TagsFilter: React.FC<Props> = ({
  filters,
  getPopupContainer,
  isContainerOpen,
  onChange,
  tags
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedTagIdsAtOpen, setSelectedTagIdsAtOpen] = React.useState<
    string[]
  >([]);
  const { dropdownHeight, dropdownPanelRef } = useProjectUserDropdownHeight(
    isOpen,
    MAX_DROPDOWN_HEIGHT
  );
  const filteredTags = filterProjectSearchTags(
    tags,
    searchQuery,
    selectedTagIdsAtOpen
  );
  const filterCount = filters.tag.length;
  const label = filterCount ? `Tags (${filterCount})` : 'Tags';
  const listHeight = Math.min(
    MAX_LIST_HEIGHT,
    filteredTags.length * ROW_HEIGHT,
    Math.max(0, dropdownHeight - FIXED_CONTENT_HEIGHT)
  );

  React.useEffect(() => {
    if (!isContainerOpen) setIsOpen(false);
  }, [isContainerOpen]);

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (nextIsOpen) setSelectedTagIdsAtOpen(filters.tag);
    setIsOpen(nextIsOpen);
  };

  const renderTag = ({ index, style }: ListChildComponentProps) => {
    const tag = filteredTags[index];

    return (
      <div style={style}>
        <label className={`${styles.assigneeRow} ${styles.compactUserRow}`}>
          <Checkbox
            checked={filters.tag.includes(tag.id)}
            onChange={() =>
              onChange({
                ...filters,
                tag: toggleToken(filters.tag, tag.id)
              })
            }
          />
          <Tag className={styles.compactTagIcon} size={16} />
          <span className={`${styles.assigneeName} ${styles.compactUserName}`}>
            {tag.label}
          </span>
        </label>
      </div>
    );
  };

  const overlay = (
    <div
      className={styles.compactDropdownPanel}
      ref={dropdownPanelRef}
      style={{ maxHeight: dropdownHeight }}
    >
      <div className={styles.tagMatchSection}>
        <Segmented
          aria-label="Tag matching"
          block
          className={styles.sortDirectionControl}
          onChange={tagMatch =>
            onChange({
              ...filters,
              tagMatch: tagMatch === 'all' ? 'all' : 'any'
            })
          }
          options={[
            { label: 'Match any', value: 'any' },
            { label: 'Match all', value: 'all' }
          ]}
          size="small"
          value={filters.tagMatch}
        />
      </div>
      <div
        className={`${styles.searchContainer} ${styles.compactSearchContainer}`}
      >
        <Input
          aria-label="Search tags"
          className={`${styles.searchInput} ${styles.compactSearchInput}`}
          onChange={event => setSearchQuery(event.target.value)}
          placeholder="Search tags..."
          prefix={<SearchOutlined />}
          value={searchQuery}
        />
      </div>
      <div className={`${styles.peopleHeading} ${styles.compactPeopleHeading}`}>
        Tags ({filteredTags.length})
      </div>
      {filteredTags.length ? (
        <FixedSizeList
          className={styles.assigneeList}
          height={listHeight}
          itemCount={filteredTags.length}
          itemSize={ROW_HEIGHT}
          width="100%"
        >
          {renderTag}
        </FixedSizeList>
      ) : (
        <p className={styles.noPeople}>No tags found</p>
      )}
    </div>
  );

  return (
    <Dropdown
      getPopupContainer={
        getPopupContainer || (trigger => trigger.parentElement || document.body)
      }
      onOpenChange={handleOpenChange}
      open={isOpen}
      overlay={overlay}
      overlayClassName={styles.compactDropdownOverlay}
      placement="bottomLeft"
      trigger={['click']}
    >
      <span className={styles.selectedDropdownTrigger}>
        {filterCount ? (
          <>
            <Button
              aria-label="Edit tags filter"
              className={styles.selectedDropdownLabel}
              shape="round"
              size="small"
              type="primary"
            >
              {label}
            </Button>
            <Button
              aria-label="Clear tags filter"
              className={styles.selectedDropdownClear}
              icon={<CloseOutlined />}
              onClick={event => {
                event.stopPropagation();
                setIsOpen(false);
                onChange({ ...filters, tag: [], tagMatch: 'any' });
              }}
              shape="circle"
              size="small"
              type="primary"
            />
          </>
        ) : (
          <Button
            aria-label={label}
            shape="round"
            size="small"
            type={isOpen ? 'primary' : 'default'}
          >
            {label}
            {isOpen ? <UpOutlined /> : <DownOutlined />}
          </Button>
        )}
      </span>
    </Dropdown>
  );
};

export default TagsFilter;
